import { ValidationHelper } from "../Engine/ValidationHelper/ValidationHelper";
import { reject } from "../../../Common/TypingUtils";

export interface DateShape {
    date: number | null | undefined;
    month: number | null | undefined;
    year: number | null | undefined;
}

export interface CalendarDateShape {
    year: number;
    month: number;
    date: number;
}

export function parseDateString(value: string): DateShape {
    const [date, month, year] = (value || "").split(".").map(x => (x ? Number(x) : undefined));
    return { date: date, month: month ? month - 1 : undefined, year: year };
}

export function isValidDate({ date, month, year }: DateShape): boolean {
    if (
        typeof date !== "number" ||
        typeof month !== "number" ||
        typeof year !== "number" ||
        year.toString().length !== 4
    ) {
        return false;
    }

    const dateObj = new Date(Date.UTC(year, month, date));

    if (dateObj.getUTCDate() === date && dateObj.getUTCMonth() === month && dateObj.getUTCFullYear() === year) {
        return true;
    }

    return false;
}

export function tryGetValidDateShape(x: DateShape): CalendarDateShape | undefined {
    if (isValidDate(x)) {
        return x as CalendarDateShape;
    }
    return undefined;
}

export function parseDateStringAsDate(value: string): Date | undefined {
    const shape = tryGetValidDateShape(parseDateString(value));
    return shape != undefined ? new Date(Date.UTC(shape.year, shape.month, shape.date)) : undefined;
}

export const comparator = (a: CalendarDateShape, b: CalendarDateShape) => {
    if (a.year < b.year) {
        return -1;
    } else if (a.year > b.year) {
        return 1;
    } else if (a.month < b.month) {
        return -1;
    } else if (a.month > b.month) {
        return 1;
    } else if (a.date < b.date) {
        return -1;
    } else if (a.date > b.date) {
        return 1;
    }
    return 0;
};

function isLess(left: CalendarDateShape, right: CalendarDateShape): boolean {
    return comparator(left, right) === -1;
}

function isEqual(a: CalendarDateShape | null | undefined, b: CalendarDateShape | null | undefined): boolean {
    return (
        (a == undefined && b == undefined) ||
        (a != undefined && b != undefined && a.year === b.year && a.month === b.month && a.date === b.date)
    );
}

function isLessOrEqual(left: CalendarDateShape, right: CalendarDateShape): boolean {
    return isLess(left, right) || isEqual(left, right);
}

export function isValidStringDate(date: string): boolean {
    return isValidDate(parseDateString(date));
}

export function isLessOrEqualStringDates(a: string, b: string): boolean {
    const dateA = tryGetValidDateShape(parseDateString(a));
    const dateB = tryGetValidDateShape(parseDateString(b));

    if (dateA && dateB) {
        return isLessOrEqual(dateA, dateB);
    }

    return false;
}

export function getLastDateOfMonth(date: Date): number {
    const lastDateOfMonth = new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0);
    return lastDateOfMonth.getDate();
}

export function differenceInMonths(leftDate: Date, rightDate: Date): number {
    const sign = rightDate > leftDate ? 1 : -1;
    const leastDate = rightDate >= leftDate ? leftDate : rightDate;
    const mostDate = rightDate >= leftDate ? rightDate : leftDate;
    const isFirstDay = leastDate.getDate() === 1;
    const isLastDay = mostDate.getDate() === getLastDateOfMonth(mostDate);

    let difference = (mostDate.getFullYear() - leastDate.getFullYear()) * 12;
    difference -= leastDate.getMonth();
    difference += mostDate.getMonth();

    const sameMonth = difference === 0;
    if (sameMonth) {
        if (isFirstDay && isLastDay) {
            return sign;
        }

        return 0;
    }

    difference--;

    if (isFirstDay) {
        difference++;
    }
    if (isLastDay) {
        difference++;
    }

    return difference * sign;
}

export function differenceInDays(leftDate: Date, rightDate: Date): number {
    const difference = (rightDate.getTime() - leftDate.getTime()) / (1000 * 3600 * 24);

    return Math.floor(difference);
}

export function addDays(date: Date, amount: number): Date {
    date.setDate(date.getDate() + amount);

    return date;
}

export function addMonths(date: Date, amount: number): Date {
    date.setMonth(date.getMonth() + amount);

    return date;
}

export function addYears(date: Date, amount: number): Date {
    date.setFullYear(date.getFullYear() + amount);

    return date;
}

export function dateToString(date: Date, format: string): string {
    const months: { [key: string]: string } = {
        "1": "января",
        "2": "февраля",
        "3": "марта",
        "4": "апреля",
        "5": "мая",
        "6": "июня",
        "7": "июля",
        "8": "августа",
        "9": "сентября",
        "10": "октября",
        "11": "ноября",
        "12": "декабря",
    };

    return format
        .replace("HH", date.getHours().toString().padStart(2, "0"))
        .replace("mm", date.getMinutes().toString().padStart(2, "0"))
        .replace("dd", date.getDate().toString().padStart(2, "0"))
        .replace("d", date.getDate().toString())
        .replace("MMMM", months[date.getMonth() + 1] ?? "")
        .replace("MM", (date.getMonth() + 1).toString().padStart(2, "0"))
        .replace("yyyy", date.getFullYear().toString());
}

export function isDate(date: unknown): date is Date {
    return date instanceof Date && !isNaN(date.getTime());
}

export function convertDate(dateValue: Date | string): Date | null {
    if (isDate(dateValue)) {
        return dateValue;
    }
    // dd.MM.yyyy
    if (ValidationHelper.isDateFormat(dateValue)) {
        const v = dateValue.split(".");
        const d = parseInt(v[0] ?? reject(), 10);
        const m = parseInt(v[1] ?? reject(), 10);
        const y = parseInt(v[2] ?? reject(), 10);

        return new Date(Date.UTC(y, m - 1, d));
    }

    // yyyy-MM-dd
    if (ValidationHelper.isDateFormatIso(dateValue)) {
        return new Date(dateValue);
    }

    return null;
}

export const HelperFunctionsDateHelpers = {
    isValidDate: isValidDate,
    parseDateString: parseDateString,
};
